import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import AddIcon from '@atlaskit/icon/glyph/add';
import { UpdateInvoiceGroupRequest } from '@atlassian/cssxp-types-billing';
import { updateInvoiceGroup } from '@atlassian/temp-proxies';
import { CustomSearch } from '@css-xp/base/custom-search';
import {
  ADVOCATE_ACCESS_TYPE,
  ADVOCATE_RESOURCE_TYPE,
  AdvocateAccessWall,
  useAdvocateAccessControl,
} from '@css-xp/ppc-access-control';
import { emptyStateProp as defaultEmptyStateProp, TableHeading, failureMessage } from '@css-xp/ppc-common';

import {
  DynamicResultTable,
  OpenSidebarButton,
  useFeatureToggle,
  useNotification,
  ContactsTooltip,
  ContactsTooltipType,
  successNotificationBuilder,
  errorNotificationBuilder,
  NotificationTypes,
} from '@css-xp/base';
import { emptyStatePropForInvoiceContactsTab, emptyStatePropForWrongSearch } from '@css-xp/ppc-common/src/state-props';
import { ADDITIONAL_INVOICE_RECIPIENT, ENABLE_DESIGN_ITERATION_1 } from '@css-xp/ppc-constants';
import { SIDEBAR } from '../../../billing-profile-details-page';
import type { FormData, InvoiceContactsProps } from './types';
import { InvoiceContactsMoreActions } from './invoice-contacts-more-actions';
import './invoice-contacts-tab.scss';

const DEFAULT_ROWS_PER_PAGE = 8;
const DEFAULT_PAGE = 1;

function getDataForCurrentPage(data: string[], currentPage: number, pageSize: number): string[] {
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  return data.slice(start, end);
}

const successNotificationOptions = successNotificationBuilder()
  .withId('update-invoice-contact-success')
  .withHeader('Update successful')
  .withMessage('Invoice contacts updated.')
  .withDismissAfter(5000)
  .isVersion(NotificationTypes.FLAG)
  .build();

const errorNotificationOptions = errorNotificationBuilder()
  .isVersion(NotificationTypes.FLAG)
  .withHeader('We’re unable to update the invoice contacts.')
  .withMessage(failureMessage())
  .withDismissAfter(5000)
  .build();

export const InvoiceContactsTab = ({
  label,
  invoiceContacts,
  loading,
  invoiceGroupId,
  transactionAccountId,
  shipToAddress,
  onComplete,
  validDomains,
}: InvoiceContactsProps) => {
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const [numberOfContacts, setNumberOfContacts] = useState<number>(0);
  const [contacts, setInvoiceContacts] = useState<string[]>(
    getDataForCurrentPage(invoiceContacts, currentPage, rowsPerPage)
  );

  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const [isContactsLoading, setIsContactsLoading] = useState<boolean>(false);
  const { sendSuccessNotification, sendErrorNotification } = useNotification();

  useEffect(() => {
    if (!loading) {
      setInvoiceContacts(getDataForCurrentPage(invoiceContacts, currentPage, rowsPerPage));
      setNumberOfContacts(invoiceContacts.length);
    }
  }, [invoiceContacts, currentPage, loading, rowsPerPage]);

  const [searching, setSearching] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const [inSearchMode, setInSearchMode] = useState(false);
  const [searchedContacts, setSearchedContacts] = useState<string[]>([]);

  const advocateHasAccessForEditingInvoiceContact = useAdvocateAccessControl({
    accessType: ADVOCATE_ACCESS_TYPE.UPDATE,
    resource: ADVOCATE_RESOURCE_TYPE.INVOICE_CONTACT,
  });

  const onSearch = (formData: FormData) => {
    setSearching(true);
    const input = formData && formData.searchTerm;
    setIsSearched(!!input);
    if (input !== '') {
      const matchedInvoiceContacts = invoiceContacts.filter((contact: string) => contact?.includes(input));
      setInvoiceContacts(getDataForCurrentPage(matchedInvoiceContacts, 1, rowsPerPage));
      setNumberOfContacts(matchedInvoiceContacts.length);
      setInSearchMode(true);
      setSearchedContacts(matchedInvoiceContacts);
    } else {
      setInvoiceContacts(getDataForCurrentPage(invoiceContacts, 1, rowsPerPage));
      setNumberOfContacts(invoiceContacts.length);
      setInSearchMode(false);
      setSearchedContacts([]);
    }
    setCurrentPage(1);
    setRowsPerPage(DEFAULT_ROWS_PER_PAGE);
    setSearching(false);
  };

  const onSetPage = (page: number) => {
    setInvoiceContacts(getDataForCurrentPage(inSearchMode ? searchedContacts : invoiceContacts, page, rowsPerPage));
    setCurrentPage(page);
  };

  const renderEditButton = useCallback(() => {
    return (
      <OpenSidebarButton
        appearance="link"
        spacing="compact"
        sidebar={SIDEBAR.UPDATE_INVOICE_RECIPIENTS}
        sidebarOptions={{
          sidebarWidth: isDesignIteration1Enabled ? undefined : '600px',
          isOverlay: !isDesignIteration1Enabled,
        }}
        payload={{
          transactionAccountId,
          invoiceGroupId: invoiceGroupId,
          recipients: contacts,
          shipToParty: shipToAddress,
          refetch: onComplete,
          validDomains: validDomains,
        }}
        testId="edit-invoice-contacts"
      >
        Edit
      </OpenSidebarButton>
    );
  }, [
    contacts,
    invoiceGroupId,
    isDesignIteration1Enabled,
    onComplete,
    shipToAddress,
    transactionAccountId,
    validDomains,
  ]);

  const handleInvoiceContactsDeletion = async (contact: string, closeInvoiceContactModal: () => void) => {
    setIsContactsLoading(true);
    try {
      const updatedContactsList = invoiceContacts.filter((invoiceContact) => invoiceContact !== contact);
      const requestData: UpdateInvoiceGroupRequest = {
        data: {
          recipients: updatedContactsList,
          shipToPartyId: shipToAddress?.id,
        },
        invoiceGroupId: invoiceGroupId as string,
        transactionAccountId: transactionAccountId as string,
      };
      await updateInvoiceGroup(requestData);
      sendSuccessNotification(successNotificationOptions);
      onComplete && onComplete();
    } catch (err) {
      sendErrorNotification(errorNotificationOptions);
    } finally {
      setIsContactsLoading(false);
      closeInvoiceContactModal();
    }
  };

  const makeRows = useCallback(() => {
    return contacts.map((contact: string, index: number) => {
      const rowKey = `billing-contact-row-${index}`;
      return {
        key: rowKey,
        cells: [
          {
            key: `${rowKey}-details`,
            content: (
              <div className="billing-contact-person-container">
                <div className="billing-contact-person">
                  <span>{contact}</span>
                </div>
              </div>
            ),
          },
          ...(advocateHasAccessForEditingInvoiceContact
            ? [
                {
                  key: `${rowKey}-actions`,
                  content: isDesignIteration1Enabled ? (
                    <InvoiceContactsMoreActions
                      invoiceContact={contact}
                      handleInvoiceContactsDeletion={handleInvoiceContactsDeletion}
                      isLoading={isContactsLoading}
                    />
                  ) : (
                    <div className="invoice-contact-actions">{renderEditButton()}</div>
                  ),
                },
              ]
            : []),
        ],
      };
    });
  }, [contacts, isContactsLoading]);

  const handleRowsChange = (rowsCount: number) => {
    setRowsPerPage(rowsCount);
    setCurrentPage(1);
    setInvoiceContacts(getDataForCurrentPage(invoiceContacts, 1, rowsCount));
  };

  const renderInvoiceContactsSearch = () => {
    if (loading) {
      return null;
    } else if (contacts?.length > 0 || isSearched) {
      return (
        <div
          className={classNames('contact-search-section', {
            'contact-search-section-v2': isDesignIteration1Enabled,
          })}
        >
          <CustomSearch
            placeholder={isDesignIteration1Enabled ? 'Search invoice contacts' : 'Search invoice recipients'}
            {...(isDesignIteration1Enabled && { helperMessage: 'Enter an email address' })}
            onSearch={onSearch}
            isClearable={true}
            isLoading={false}
          />
        </div>
      );
    }
  };

  const renderActionsOnTop = useCallback(() => {
    return [
      () => (
        <>
          <div
            className={classNames({
              'invoice-contact-actions add': !isDesignIteration1Enabled,
              'invoice-contact-actions-v2': isDesignIteration1Enabled,
            })}
          >
            <AdvocateAccessWall
              accessType={ADVOCATE_ACCESS_TYPE.CREATE}
              resource={ADVOCATE_RESOURCE_TYPE.INVOICE_CONTACT}
            >
              <div className="add-action">
                <OpenSidebarButton
                  appearance="default"
                  sidebar={SIDEBAR.UPDATE_INVOICE_RECIPIENTS}
                  sidebarOptions={{
                    sidebarWidth: isDesignIteration1Enabled ? undefined : '600px',
                    isOverlay: !isDesignIteration1Enabled,
                    sidebarTitle: isDesignIteration1Enabled ? 'Add invoice contact' : undefined,
                  }}
                  iconBefore={<AddIcon label="Add icon" size="small" />}
                  payload={{
                    transactionAccountId,
                    invoiceGroupId: invoiceGroupId,
                    recipients: invoiceContacts,
                    shipToParty: shipToAddress,
                    refetch: onComplete,
                    validDomains: validDomains,
                  }}
                  testId="add-invoice-contacts"
                >
                  {isDesignIteration1Enabled ? 'Invoice contact' : 'Add an invoice recipient'}
                </OpenSidebarButton>
              </div>
            </AdvocateAccessWall>
          </div>
        </>
      ),
    ];
  }, [invoiceContacts, transactionAccountId, invoiceGroupId, shipToAddress, onComplete, isDesignIteration1Enabled]);

  const isDesignIteration1EnabledAndNoContacts = isDesignIteration1Enabled && !isSearched && numberOfContacts === 0;

  const renderTableHeading = () => {
    if (isDesignIteration1Enabled || isDesignIteration1EnabledAndNoContacts) return null;

    return <TableHeading>{label}</TableHeading>;
  };

  const emptyStatePropForSearch = isDesignIteration1Enabled ? emptyStatePropForWrongSearch : defaultEmptyStateProp;
  const emptyStateProps = isDesignIteration1Enabled
    ? emptyStatePropForInvoiceContactsTab(renderActionsOnTop())
    : defaultEmptyStateProp;

  const makeHead = () => {
    if (numberOfContacts === 0) {
      return { cells: [] };
    }
    return {
      cells: [
        {
          content: 'Contact',
        },
        ...(advocateHasAccessForEditingInvoiceContact ? [{ content: 'Actions' }] : []),
      ],
    };
  };

  return (
    <div className="billing-contacts-container">
      <div
        className={classNames('billing-contacts-table', {
          'billing-contacts-table-v2': isDesignIteration1Enabled,
        })}
      >
        <DynamicResultTable
          head={isDesignIteration1Enabled ? makeHead() : undefined}
          beforeTable={renderTableHeading()}
          rows={[...makeRows()]}
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          loadingSpinnerSize="large"
          emptyStateProps={isSearched ? emptyStatePropForSearch : emptyStateProps}
          totalCount={numberOfContacts}
          onPageChange={onSetPage}
          isPaginationEnabled={numberOfContacts > DEFAULT_ROWS_PER_PAGE}
          onRowChange={handleRowsChange}
          actions={isDesignIteration1EnabledAndNoContacts ? undefined : renderActionsOnTop()}
          boxShadow={false}
          className="billing-contacts-table"
          alternateRowColor={false}
          showFilters={true}
        >
          {renderInvoiceContactsSearch()}
        </DynamicResultTable>
      </div>
    </div>
  );
};
